<template>
    <svg
        class="glifo"
        width="1em"
        height="1em"
        viewBox="0 0 100 100"
        aria-hidden="true"
        focusable="false"
        fill="none"
        stroke="currentColor"
        stroke-width="9.70"
        stroke-linecap="butt"
        stroke-linejoin="miter"
    >
        <path v-for="(d, i) in trazos" :key="i" :d="d"/>
    </svg>
</template>

<script>
/*
 * Glifos del producto — dibujados con las reglas de la sección 06 del manual
 * de marca (storage/app/manual-de-marca.html):
 *
 *   Caja        100 × 100, cuadrada
 *   Trazo       9.70 · = asta de CYNT · 0.097 × lado
 *   Ángulos     múltiplos de 30° · retícula hexagonal
 *   Excepción   lupa y visto, a 45° (el manual las contempla explícitamente)
 *   Terminales  a hueso · vértices en inglete  (butt / miter)
 *
 * El manual también avisa por qué el trazo no se adelgaza: «si un glifo pide un
 * trazo aún más fino, el problema no es el trazo, es que lleva demasiada
 * información — se simplifica el dibujo, no se adelgaza la línea». Por eso
 * ninguno pasa de cuatro trazos.
 *
 * Van inline y con stroke="currentColor" a propósito: así heredan el color de
 * quien los contiene —el enlace del sidebar, el nodo del editor— y siguen sus
 * estados. Con un <img> el color quedaría congelado.
 *
 * El width/height de 1em NO es decorativo: un <svg> con viewBox pero sin
 * medidas propias mide 300 × 150 px por defecto. Sin ese tamaño intrínseco el
 * glifo depende por completo de que le llegue su clase, y donde no llegue
 * revienta el contenedor — en el nodo del editor, que no lleva overflow
 * hidden, se lo lleva puesto. Con 1em cae siempre en algo sensato y las clases
 * sólo ajustan.
 */
const GLIFOS = {

    /* ── Navegación (sidebar) ─────────────────────────────────────────── */

    // Tres barras: la lectura de un tablero de cifras.
    panel: [
        'M22 82 L22 56',
        'M50 82 L50 24',
        'M78 82 L78 42',
    ],
    // El visto. Una de las dos excepciones a los 30° que el manual permite.
    tareas: [
        'M18 52 L40 74 L82 32',
    ],
    // Dos correderas: lo que se ajusta.
    administracion: [
        'M14 36 L86 36',
        'M60 22 L60 50',
        'M14 68 L86 68',
        'M36 54 L36 82',
    ],
    // La celda como cabeza sobre los hombros. La persona se dibuja con el
    // hexágono de la marca y no con un círculo, que no existe en este sistema.
    usuarios: [
        'M68 30 L59 45.59 L41 45.59 L32 30 L41 14.41 L59 14.41 Z',
        'M18 84 L31.86 60 L68.14 60 L82 84',
    ],
    // Una retícula de bloques: la disposición, no los datos.
    paneles: [
        'M12 20 L88 20 L88 80 L12 80 Z',
        'M46 20 L46 80',
        'M46 50 L88 50',
    ],
    // La línea de tendencia, a 30° exactos en los tres tramos.
    reportes: [
        'M14 70 L38 56.14 L58 67.69 L86 51.52',
    ],
    // La celda en capas: el mismo hexágono de la marca, dividido.
    datos: [
        'M86 50 L68 81.18 L32 81.18 L14 50 L32 18.82 L68 18.82 Z',
        'M30 36 L70 36',
        'M30 64 L70 64',
    ],
    // La hoja de calendario con sus dos anillas.
    reservas: [
        'M16 30 L84 30 L84 86 L16 86 Z',
        'M16 48 L84 48',
        'M34 12 L34 30',
        'M66 12 L66 30',
    ],

    /* ── Tipos de nodo (editor de flujos) ─────────────────────────────────
       Los nombres son los typeObject del flujo, para que el editor los pida
       directo por tipo sin tabla intermedia. */

    // Inicio: el triángulo de reproducción, con sus lados a 30°.
    start: [
        'M24 14.2 L86 50 L24 85.8 Z',
    ],
    // Entrada: el dato que baja al campo.
    input: [
        'M50 12 L50 66.7',
        'M28 54 L50 66.7 L72 54',
        'M16 86 L84 86',
    ],
    // Servicio: la petición que sale y la respuesta que vuelve — que es lo que
    // hace este nodo: llamar a un webservice. Cada flecha es un solo trazo con
    // dos subtrazos (eje y punta), para no gastar cuatro paths en dos flechas.
    process: [
        'M18 32 L76 32 M56 20.45 L76 32 L56 43.55',
        'M82 68 L24 68 M44 56.45 L24 68 L44 79.55',
    ],
    // Salida: la flecha que sale, con la punta a 30°.
    output: [
        'M14 50 L84 50',
        'M60 36.14 L84 50 L60 63.86',
    ],
    // Condición: la bifurcación, a 60°.
    condition: [
        'M50 88 L50 56 L30 21.36',
        'M50 56 L70 21.36',
    ],
    // Usuario: el mismo glifo que en la navegación — es el mismo concepto.
    setuser: [
        'M68 30 L59 45.59 L41 45.59 L32 30 L41 14.41 L59 14.41 Z',
        'M18 84 L31.86 60 L68.14 60 L82 84',
    ],
    // OCR: la lupa. Lente hexagonal, porque el círculo no existe en la marca,
    // y mango a 45° — la otra excepción que el manual contempla.
    ocr: [
        'M68 40 L55 62.52 L29 62.52 L16 40 L29 17.48 L55 17.48 Z',
        'M55 62.52 L81 88.52',
    ],
    // Cyntia: los tres muros del símbolo de plataforma, sin la celda alrededor.
    // No es un dibujo nuevo: el manual dice que Intelligence «es el interior del
    // símbolo… el motor visto por dentro, y por eso es el único que se dibuja
    // con una pieza que ya existe en la marca».
    ia: [
        'M84.77 9.85 L61.59 50 L84.77 90.15',
        'M61.59 50 L15.23 50',
    ],
    // Subtareas: el tronco y las ramas que cuelgan de él.
    subtask: [
        'M26 14 L26 74',
        'M26 44 L74 44',
        'M26 74 L74 74',
    ],
    // Reserva: la misma hoja de calendario que en la navegación.
    reserva: [
        'M16 30 L84 30 L84 86 L16 86 Z',
        'M16 48 L84 48',
        'M34 12 L34 30',
        'M66 12 L66 30',
    ],
    // Selección de ítems: la matriz ítem × origen que el nodo compara.
    itemsel: [
        'M18 34 L44 34',
        'M56 34 L82 34',
        'M18 66 L44 66',
        'M56 66 L82 66',
    ],
};

export default {
    name: 'Glifo',
    props: {
        name: { type: String, required: true },
    },
    computed: {
        trazos() {
            return GLIFOS[this.name] || [];
        },
    },
};
</script>
